/**
 * DefaultAppRedirect component
 * Auto-redirects users to their default app's chat page.
 * Shown at /app route instead of the old apps listing page.
 */

import { Navigate, useLocation } from 'react-router-dom'
import { useDefaultApp } from '@/hooks/use-default-app'

/**
 * Loading spinner shown while resolving the default app
 */
function LoadingState() {
  return (
    <div className="flex min-h-dvh w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <p className="text-sm text-muted-foreground">Loading your workspace...</p>
      </div>
    </div>
  )
}

/**
 * Shown when the workspace has no apps installed
 */
function NoAppsState() {
  return (
    <div className="flex min-h-dvh w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-6 max-w-md text-center px-6">
        <div className="size-16 rounded-2xl bg-primary/10 flex items-center justify-center">
          <span className="text-3xl">📱</span>
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-2">No apps yet</h2>
          <p className="text-sm text-muted-foreground">
            Install your first app from the marketplace to get started with AI agents.
          </p>
        </div>
        <a
          href="/app/apps"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors"
        >
          Browse Marketplace
        </a>
      </div>
    </div>
  )
}

/**
 * Error state shown when apps failed to load for a reason other than auth
 * (e.g. server error, network issue). Distinct from the empty-workspace state —
 * we don't want to tell the user "install an app" when the real problem is a 500.
 */
function LoadFailedState({ message }: { message: string }) {
  return (
    <div className="flex min-h-dvh w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-6 max-w-md text-center px-6">
        <div className="size-16 rounded-2xl bg-destructive/10 flex items-center justify-center">
          <span className="text-3xl">⚠️</span>
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Couldn't load your apps</h2>
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  )
}

/**
 * DefaultAppRedirect — resolves the default app and redirects to its chat page
 */
export function DefaultAppRedirect() {
  const { appId, isLoading, hasNoApps, error } = useDefaultApp()
  const location = useLocation()

  if (isLoading) {
    return <LoadingState />
  }

  // Real failure takes priority over the "empty workspace" UI — showing the
  // marketplace prompt on a server error is the exact bug we fixed upstream.
  if (error) {
    return <LoadFailedState message={error} />
  }

  if (hasNoApps || !appId) {
    return <NoAppsState />
  }

  // Carry the query string through. `/app` is the origin-agnostic entry point
  // external links and post-auth redirects point at (e.g. signup's
  // ?invitationId=…), and dropping the params here would
  // silently swallow that intent.
  return <Navigate to={`/app/${appId}/chat${location.search}`} replace />
}
